import { buildDocumentAgentOutputProcessLines } from './documentAgentOutputProcessLines.js';
import {
  normalizeRunState,
  resolveArray,
  text,
  workflowProgressProjection,
  workflowProjection,
  workflowRunEvents,
  workflowRunState,
  workflowRunSteps,
} from './workflowRunProjectionState.js';

const INTERNAL_TOKEN_PATTERN = /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/i;

function firstTruthy(...values) {
  return values.find(Boolean);
}

function line(status = '', label = '') {
  return status ? `[${status}] ${label}` : label;
}

function publicStepLabel(step = {}, index = 0) {
  const candidate = text(firstTruthy(step.visible_label, step.label, step.title));
  const identifiers = [
    text(step.step_id),
    text(step.capability_id),
    text(step.owner_capability),
  ].filter(Boolean).map((identifier) => identifier.toLowerCase());
  const normalizedCandidate = candidate.toLowerCase();
  const exposesIdentity = identifiers.some((identifier) => normalizedCandidate.includes(identifier));
  const exposesInternalToken = INTERNAL_TOKEN_PATTERN.test(candidate);
  return candidate && !exposesIdentity && !exposesInternalToken ? candidate : `步骤 ${index + 1}`;
}

function stepEntry(step = {}, index = 0) {
  return {
    line: line(normalizeRunState(step.status), publicStepLabel(step, index)),
    scope: 'business',
    stepId: text(firstTruthy(step.step_id, step.node_id)),
  };
}

function progressStepEntries(progress = {}) {
  return resolveArray(progress.steps).map((step, index) => (
    stepEntry(step, index)
  ));
}

function legacyStepEntries(item = {}) {
  return workflowRunSteps(item).map((step, index) => (
    stepEntry(step, index)
  ));
}

function admittedArtifactLines(item = {}) {
  return workflowRunEvents(item)
    .filter(({ event_name: eventName }) => eventName === 'artifact.admitted')
    .map(({ payload = {} }) => {
      const title = text(firstTruthy(payload.artifact_title, payload.title, payload.label));
      const producer = text(firstTruthy(payload.producer_label, payload.capability_label, payload.step_label));
      return line('completed', [
        '产物已接纳',
        title,
        producer ? `来源 ${producer}` : '',
      ].filter(Boolean).join(' · '));
    });
}

export function buildWorkflowRunProcessLines(item = {}) {
  const outputLines = buildDocumentAgentOutputProcessLines(workflowProjection(item));
  const stepLines = buildWorkflowRunProcessEntries(item).map(({ line: processLine }) => processLine);
  return [...stepLines, ...admittedArtifactLines(item), ...outputLines];
}

export function buildWorkflowRunProcessEntries(item = {}) {
  const progress = workflowProgressProjection(item);
  return progress === null ? legacyStepEntries(item) : progressStepEntries(progress);
}

export function resolveWorkflowRunStatus(item = {}) {
  const explicitStatus = workflowRunState(item);
  if (explicitStatus || workflowProgressProjection(item) !== null) {
    return explicitStatus;
  }
  return '';
}

export function hasWorkflowRunProgress(item = {}) {
  const projection = workflowProjection(item);
  return [
    workflowProgressProjection(item) !== null,
    workflowRunSteps(item).length > 0,
    Boolean(text(firstTruthy(projection.run_state, projection.runState, projection.status))),
  ].some(Boolean);
}
